<script setup>
import { ref, computed } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useMessagingStore } from '@/stores/messaging.store'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import rmLogo from '@/assets/logo.svg'
import { getRestaurantImageUrl } from '@core/utils/restaurantImage'

const props = defineProps({
  activeConversationId: {
    type: String,
    default: null,
  },
})

const emit = defineEmits(['select', 'new'])

const messagingStore = useMessagingStore()
const { conversations, loadingConversations } = storeToRefs(messagingStore)

// Feature flag: restaurant messaging is not yet available
const RESTAURANT_MESSAGING_ENABLED = false

const filter = ref('all') // all | members | restaurants
const searchQuery = ref('')

const filtered = computed(() => {
  let convs = conversations.value
  if (filter.value === 'members') convs = convs.filter(c => c.type === 'member-member')
  if (filter.value === 'restaurants') convs = convs.filter(c => c.type === 'member-restaurant')
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    convs = convs.filter(c => conversationTitle(c).toLowerCase().includes(q))
  }
  
  return convs
})

function conversationTitle(conv) {
  if (conv.type === 'member-support') {
    return 'RM Support'
  }
  if (conv.type === 'member-restaurant') {
    return conv.restaurant?.name || 'Restaurant'
  }
  const others = (conv.participants || [])
  if (others.length === 1) {
    return `${others[0].firstName || ''} ${others[0].lastName || ''}`.trim()
  }

  return others.map(p => p.firstName).filter(Boolean).join(', ')
}

function conversationSubtitle(conv) {
  if (conv.type === 'member-support') {
    return ''
  }
  if (conv.type === 'member-restaurant') {
    return conv.restaurant?.market || ''
  }
  const others = (conv.participants || [])
  if (others.length === 1) {
    const p = others[0]

    return [p.title, p.company].filter(Boolean).join(' at ')
  }

  return ''
}

function conversationAvatar(conv) {
  if (conv.type === 'member-support') return null
  if (conv.type === 'member-restaurant') {
    return getRestaurantImageUrl(conv.restaurant, { includeEventSpaces: false })
  }
  const others = conv.participants || []
  const imgId = others[0]?.profileImageId

  return imgId ? mediaUrl(imgId, 'thumb') : null
}

function avatarInitials(conv) {
  if (conv.type === 'member-support') return 'RM'
  if (conv.type === 'member-restaurant') return ''
  const others = conv.participants || []
  if (others.length === 1) {
    return `${others[0].firstName?.[0] || ''}${others[0].lastName?.[0] || ''}`
  }

  return ''
}

function lastMessagePreview(conv) {
  if (!conv.lastMessage?.text) return 'No messages yet'
  const text = conv.lastMessage.text
  
  return text.length > 70 ? text.substring(0, 70) + '…' : text
}

function lastMessageTime(conv) {
  if (!conv.lastMessageAt) return ''
  try {
    const m = moment(conv.lastMessageAt)
    if (m.isSame(moment(), 'day')) return m.format('h:mm A')
    if (m.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
    if (m.isAfter(moment().subtract(7, 'days'))) return m.format('ddd')
    
    return m.format('MMM D')
  } catch {
    return ''
  }
}

function hasUnread(conv) {
  // Unread count for current user is tracked in store; for simplicity check lastMessage not from me
  return conv.unread > 0
}
</script>

<template>
  <div class="messaging-inbox d-flex flex-column h-100">
    <!-- Header -->
    <div class="inbox-header pa-3">
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-subtitle-1 font-weight-semibold">Messaging</span>
        <VBtn
          icon
          size="small"
          color="primary"
          variant="tonal"
          @click="emit('new')"
        >
          <VIcon size="18">
            mdi-pencil-plus-outline
          </VIcon>
        </VBtn>
      </div>

      <!-- Search -->
      <VTextField
        v-model="searchQuery"
        placeholder="Search messages"
        prepend-inner-icon="mdi-magnify"
        variant="solo-filled"
        density="compact"
        hide-details
        class="inbox-search"
        clearable
        flat
        rounded
      />
    </div>

    <!-- Filter chips -->
    <div class="px-3 pb-2 d-flex gap-2">
      <VChip
        v-for="f in (RESTAURANT_MESSAGING_ENABLED ? [{ value: 'all', label: 'All' }, { value: 'members', label: 'Members' }, { value: 'restaurants', label: 'Restaurants' }] : [{ value: 'all', label: 'All' }])"
        :key="f.value"
        size="small"
        :color="filter === f.value ? 'primary-dark' : undefined"
        :variant="filter === f.value ? 'flat' : 'outlined'"
        @click="filter = f.value"
      >
        {{ f.label }}
      </VChip>
    </div>

    <!-- List -->
    <div class="inbox-list flex-1 overflow-y-auto">
      <div
        v-if="loadingConversations"
        class="d-flex justify-center py-8"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="28"
        />
      </div>

      <div
        v-else-if="filtered.length === 0"
        class="text-center text-medium-emphasis py-8 px-4"
      >
        <VIcon
          size="36"
          class="mb-2"
        >
          mdi-message-outline
        </VIcon>
        <div class="text-body-2 mb-3">
          {{ searchQuery ? 'No results found' : 'No conversations yet' }}
        </div>
        <VBtn
          v-if="!searchQuery"
          variant="tonal"
          color="primary"
          size="small"
          @click="emit('new')"
        >
          Start a conversation
        </VBtn>
      </div>

      <div
        v-for="conv in filtered"
        :key="conv._id"
        class="inbox-item d-flex align-start pa-3 cursor-pointer"
        :class="{ 'inbox-item--active': activeConversationId === conv._id }"
        @click="emit('select', conv._id)"
      >
        <!-- Avatar -->
        <VAvatar
          v-if="conv.type === 'member-support'"
          size="44"
          class="mr-3 flex-shrink-0"
          color="white"
        >
          <VImg
            :src="rmLogo"
            cover
          />
        </VAvatar>
        <VAvatar
          v-else
          size="44"
          class="mr-3 flex-shrink-0"
          :color="conv.type === 'member-restaurant' ? 'primary-light' : 'grey-lighten-3'"
        >
          <VImg
            v-if="conversationAvatar(conv)"
            :src="conversationAvatar(conv)"
            cover
          />
          <VIcon
            v-else-if="conv.type === 'member-restaurant'"
            color="primary"
          >
            mdi-silverware-fork-knife
          </VIcon>
          <span
            v-else-if="avatarInitials(conv)"
            class="text-caption font-weight-medium"
            style="color: var(--ds-brand-primary-dark, #3F998F);"
          >{{ avatarInitials(conv) }}</span>
          <VIcon
            v-else
            color="grey"
          >
            mdi-account
          </VIcon>
        </VAvatar>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <div class="d-flex align-baseline justify-space-between gap-1">
            <span
              class="text-body-2 font-weight-semibold text-truncate"
              :class="{ 'text-primary-dark': hasUnread(conv) }"
            >
              {{ conversationTitle(conv) }}
            </span>
            <span class="text-caption text-medium-emphasis flex-shrink-0">
              {{ lastMessageTime(conv) }}
            </span>
          </div>
          <div
            v-if="conversationSubtitle(conv)"
            class="text-caption text-medium-emphasis text-truncate"
          >
            {{ conversationSubtitle(conv) }}
          </div>
          <div class="d-flex align-center justify-space-between mt-1">
            <span
              class="text-caption text-truncate"
              :class="hasUnread(conv) ? 'font-weight-medium' : 'text-medium-emphasis'"
            >
              {{ lastMessagePreview(conv) }}
            </span>
            <VBadge
              v-if="hasUnread(conv)"
              :content="conv.unread"
              color="primary"
              class="flex-shrink-0 ml-1"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.messaging-inbox {
  height: 100%;
  border-right: 1px solid var(--ds-border-default);
}

.inbox-header {
  border-bottom: 1px solid var(--ds-border-default);
  flex-shrink: 0;
}

.inbox-search :deep(.v-field) {
  font-size: 16px;
  background: var(--ds-surface-alt, #f8f8f9);
}

.inbox-list {
  overflow-y: auto;
}

.inbox-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background 0.12s;
}

.inbox-item:hover {
  background: var(--ds-surface-alt, #f8f8f9);
}

.inbox-item--active {
  background: var(--ds-brand-primary-light, #e9f4f3);
}

.min-w-0 {
  min-width: 0;
}

.text-primary-dark {
  color: var(--ds-brand-primary-dark, #3F998F);
}
</style>
